import { Link, useLocation } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

const AccuracyPage = () => {
  const location = useLocation();
  const isEnglish = location.pathname.startsWith('/en/');

  const TEXT_JA = {
    breadcrumb: { home: 'ホーム', troubleshooting: 'トラブル対策', title: '寸法精度不良' },
    badge: '軽微',
    title: '寸法精度不良',
    description: '設計値と実測値の差異。機械的な調整とスライサー設定の両面からアプローチ。',
    symptoms: {
      title: '症状の例',
      items: [
        '設計値より大きく（または小さく）造形される',
        '穴径が設計値と合わない',
        'パーツ同士が嵌合しない',
        'X/Y/Z軸で誤差が異なる'
      ]
    },
    causes: {
      title: '主な原因',
      items: [
        { title: '1. フロー率の不適切', desc: 'フロー率が高すぎると造形物が大きくなり、低すぎると小さくなります。' },
        { title: '2. ベルトの緩み', desc: 'X/Y軸のベルトが緩んでいると、位置決め精度が低下し、寸法誤差が発生します。' },
        { title: '3. 温度変動', desc: 'ノズル温度やベッド温度の変動により、樹脂の収縮率が変わり、寸法誤差が生じます。' },
        { title: '4. ステップ/mm設定の誤り', desc: 'プリンターのステップ/mm設定が正しくないと、全体的な寸法誤差が発生します。' }
      ]
    },
    solutions: {
      title: '対策・チェックポイント',
      items: [
        {
          title: 'フロー率の調整',
          desc: 'キャリブレーションキューブで測定して調整します。',
          points: ['20mm立方体を造形して実測', '誤差に応じてフロー率を調整（通常95-105%）', '外壁とインフィルで個別調整も可能']
        },
        {
          title: 'ベルトの張り調整',
          desc: '適切なテンションで位置決め精度を確保します。',
          points: ['ベルトを指で弾いて音を確認（低い音が理想）', '緩すぎず、張りすぎない程度に調整', 'X軸とY軸のテンションを揃える']
        },
        {
          title: '温度の安定化',
          desc: '一定の温度を維持することで収縮率を安定させます。',
          points: ['PIDチューニングの実施', '室温の変動を避ける', 'エンクロージャーで庫内温度を一定に']
        },
        {
          title: 'ステップ/mmのキャリブレーション',
          desc: '各軸の移動量を正確に設定します。',
          points: ['100mm移動させて実測', '誤差に応じてステップ/mm値を計算', 'ファームウェアに設定を保存']
        },
        {
          title: '水平補正（Horizontal Expansion）',
          desc: 'スライサーの補正機能で寸法を調整します。',
          points: ['穴が小さい場合: 正の値（+0.1〜+0.2mm）', '外形が大きい場合: 負の値（-0.1〜-0.2mm）', '穴径補正（Hole Horizontal Expansion）も活用']
        },
        {
          title: '冷却の最適化',
          desc: '適切な冷却で収縮を均一にします。',
          points: ['小さなパーツでは冷却強化', '大きなパーツでは冷却を控えめに', '材料に応じた冷却設定']
        }
      ]
    },
    calibration: {
      title: '寸法精度キャリブレーション手順',
      steps: [
        { title: '1. キャリブレーションキューブの造形', desc: '20mm × 20mm × 20mmの立方体を造形します。' },
        { title: '2. 実測', desc: 'ノギスで各辺を測定し、誤差を記録します。' },
        { title: '3. フロー率の計算', desc: '新フロー率 = 現フロー率 × (設計値 / 実測値)' },
        { title: '4. 再テスト', desc: '調整後、再度キューブを造形して確認します。' }
      ]
    },
    related: {
      title: '関連情報',
      items: [
        { title: 'PLA フィラメント', desc: '収縮率が低く寸法精度が出やすい', link: '/filament/pla' },
        { title: 'ABS フィラメント', desc: '収縮率が高く寸法管理が難しい', link: '/filament/abs' }
      ]
    },
    backLink: 'トラブル対策一覧に戻る'
  };

  const TEXT_EN = {
    breadcrumb: { home: 'Home', troubleshooting: 'Troubleshooting', title: 'Dimensional Accuracy' },
    badge: 'Minor',
    title: 'Dimensional Accuracy',
    description: 'Discrepancy between design and actual measurements. Approach from both mechanical adjustment and slicer settings.',
    symptoms: {
      title: 'Symptoms',
      items: [
        'Print larger (or smaller) than design',
        'Hole diameters don\'t match design',
        'Parts don\'t fit together',
        'Different errors on X/Y/Z axes'
      ]
    },
    causes: {
      title: 'Main Causes',
      items: [
        { title: '1. Improper Flow Rate', desc: 'Too high flow rate makes prints larger, too low makes them smaller.' },
        { title: '2. Loose Belts', desc: 'Loose X/Y axis belts reduce positioning accuracy, causing dimensional errors.' },
        { title: '3. Temperature Fluctuation', desc: 'Variations in nozzle or bed temperature change shrinkage rate, causing dimensional errors.' },
        { title: '4. Incorrect Steps/mm Setting', desc: 'Wrong printer steps/mm settings cause overall dimensional errors.' }
      ]
    },
    solutions: {
      title: 'Solutions & Checkpoints',
      items: [
        {
          title: 'Adjust Flow Rate',
          desc: 'Measure with calibration cube and adjust.',
          points: ['Print 20mm cube and measure', 'Adjust flow rate based on error (typically 95-105%)', 'Can adjust walls and infill separately']
        },
        {
          title: 'Adjust Belt Tension',
          desc: 'Ensure positioning accuracy with proper tension.',
          points: ['Pluck belt and check sound (low tone is ideal)', 'Adjust to not too loose, not too tight', 'Match tension on X and Y axes']
        },
        {
          title: 'Stabilize Temperature',
          desc: 'Maintain constant temperature to stabilize shrinkage.',
          points: ['Perform PID tuning', 'Avoid room temperature fluctuations', 'Use enclosure to maintain chamber temperature']
        },
        {
          title: 'Calibrate Steps/mm',
          desc: 'Set accurate movement for each axis.',
          points: ['Move 100mm and measure actual', 'Calculate steps/mm based on error', 'Save settings to firmware']
        },
        {
          title: 'Horizontal Expansion',
          desc: 'Adjust dimensions with slicer compensation.',
          points: ['Small holes: positive value (+0.1~+0.2mm)', 'Large outer dimensions: negative value (-0.1~-0.2mm)', 'Use Hole Horizontal Expansion feature']
        },
        {
          title: 'Optimize Cooling',
          desc: 'Uniform shrinkage with appropriate cooling.',
          points: ['Enhance cooling for small parts', 'Reduce cooling for large parts', 'Cooling settings per material']
        }
      ]
    },
    calibration: {
      title: 'Dimensional Accuracy Calibration Steps',
      steps: [
        { title: '1. Print Calibration Cube', desc: 'Print a 20mm × 20mm × 20mm cube.' },
        { title: '2. Measure', desc: 'Measure each side with calipers and record errors.' },
        { title: '3. Calculate Flow Rate', desc: 'New flow rate = Current flow rate × (Design value / Measured value)' },
        { title: '4. Retest', desc: 'After adjustment, print cube again to verify.' }
      ]
    },
    related: {
      title: 'Related Information',
      items: [
        { title: 'PLA Filament', desc: 'Low shrinkage, good dimensional accuracy', link: '/filament/pla' },
        { title: 'ABS Filament', desc: 'High shrinkage, difficult dimensional control', link: '/filament/abs' }
      ]
    },
    backLink: 'Back to Troubleshooting List'
  };

  const TEXT = isEnglish ? TEXT_EN : TEXT_JA;
  const prefix = isEnglish ? '/en' : '';

  const breadcrumbItems = [
    { label: TEXT.breadcrumb.home, path: `${prefix}/` },
    { label: TEXT.breadcrumb.troubleshooting, path: `${prefix}/troubleshooting` },
    { label: TEXT.breadcrumb.title }
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid" data-lang={isEnglish ? 'en' : 'ja'}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <span className="tech-label bg-[#5f8d63] text-white">{TEXT.badge}</span>
                <h1 className="text-4xl font-bold text-white font-['Exo']">
                  {TEXT.title}
                </h1>
              </div>
              <p className="text-metal-light text-lg">
                {TEXT.description}
              </p>
              <div className="section-divider"></div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.symptoms.title}
              </h2>
              <ul className="space-y-3 text-metal-light">
                {TEXT.symptoms.items.map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <i className="ri-arrow-right-s-line text-accent mt-1"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.causes.title}
              </h2>
              <div className="space-y-4">
                {TEXT.causes.items.map((item, idx) => (
                  <div key={idx} className="border-l-4 border-l-[#5f8d63] pl-4">
                    <h3 className="text-lg font-semibold text-accent font-['Exo'] mb-2">
                      {item.title}
                    </h3>
                    <p className="text-metal-light text-sm">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.solutions.title}
              </h2>
              <div className="space-y-4">
                {TEXT.solutions.items.map((item, idx) => (
                  <div key={idx} className="clickable-card p-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-check-line text-[#5f8d63] text-xl mt-1"></i>
                      <div>
                        <h3 className="text-lg font-semibold text-white font-['Exo'] mb-2">
                          {item.title}
                        </h3>
                        <p className="text-metal-light text-sm mb-2">
                          {item.desc}
                        </p>
                        <ul className="text-metal-light text-sm space-y-1 ml-4">
                          {item.points.map((point, pidx) => (
                            <li key={pidx}>• {point}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.calibration.title}
              </h2>
              <div className="space-y-4">
                {TEXT.calibration.steps.map((step, idx) => (
                  <div key={idx} className="border-l-4 border-l-accent pl-4">
                    <h3 className="text-white font-semibold mb-2">{step.title}</h3>
                    <p className="text-metal-light text-sm">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="static-card p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4">
                {TEXT.related.title}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {TEXT.related.items.map((item, idx) => (
                  <Link
                    key={idx}
                    to={`${prefix}${item.link}`}
                    className="clickable-card p-4 hover:border-l-4 hover:border-l-accent transition-all"
                  >
                    <h3 className="text-white font-['Exo'] font-semibold mb-1">
                      {item.title}
                    </h3>
                    <p className="text-metal-light text-sm">
                      {item.desc}
                    </p>
                  </Link>
                ))}
              </div>
            </div>

            <div className="flex justify-center">
              <Link
                to={`${prefix}/troubleshooting`}
                className="flex items-center space-x-2 text-accent hover:text-white transition-colors font-['Roboto_Mono']"
              >
                <i className="ri-arrow-left-line"></i>
                <span>{TEXT.backLink}</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AccuracyPage;
